import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link } from 'react-router-dom';
import { Stack, Tabs, TabsList, TabsTrigger, TabsContent, Text, Spinner } from '@nike/podium';
import { CategorySection, DetailAccordion, DetailSection, HighlightedSection, StackedDetail } from './EntityOverviewComponents';
import { TeamMemberList } from '~/components/entityDetail/TeamMemberList';
import { buildExpressionTeamMembers, formatUpcharge } from '~/utils/entityDetailUtils';
import { useExpressionProjects } from '~/atoms/hooks/useExpressionProjects';
import { ProjectMiniCard } from '~/components/cardView/ProjectMiniCard';
const CUSTOM_LABELS = {
    expressionName: 'Expression Name',
    expressionType: 'Expression Type',
    expressionIntent: 'Expression Intent',
    specialEdition: 'Special Edition',
    fieldOfPlay: 'Field of Play',
    styleType: 'Style Type',
    materialType: 'Material Type',
    spectrumType: 'Spectrum Type',
    distributionIntent: 'Distribution Intent',
    keyDoors: 'Key Doors',
    keyGeo: 'Key Geo',
    keyApparelConnect: 'Key Apparel Connect',
    mustHaves: 'Must Haves',
    materialDetails: 'Material Details',
    colorDetails: 'Color Details',
    graphicDetails: 'Graphic Details',
    sustainabilityTierGoal: 'Sustainability Tier Goal',
    sustainabilityFocus: 'Sustainability Focus',
    keyLearnings: 'Key Learnings',
    geoAdoption: 'Geo Adoption',
    keyDistro: 'Key Distribution',
    museDoors: 'Muse Doors',
    genderStrategy: 'Gender Strategy',
    isXDimension: 'X-Dimension',
    hasApparelConnection: 'Apparel Connection',
    modelMixCount: 'Model Mix Count',
    targetConsumerElements: 'Target Consumer',
    marketplaceInsightElements: 'Marketplace Insights',
    hindsightCompElements: 'Hindsight Comp',
    referenceExpressions: 'Reference Expressions',
    upcharge: 'Upcharge'
};
const DETAILS_SECTION_CONFIG = [
    {
        id: 'taxonomy',
        title: 'Taxonomy',
        fields: ['brand', 'division', 'fieldOfPlay', 'gender', 'age', 'seasons']
    },
    {
        id: 'style-details',
        title: 'Style Details',
        fields: ['styleType', 'materialType', 'spectrumType']
    },
    {
        id: 'distribution',
        title: 'Distribution',
        fields: [
            'distributionIntent',
            'keyDoors',
            'lifecycle',
            'keyGeo',
            'keyApparelConnect'
        ],
        displayType: 'stacked'
    },
    {
        id: 'product-details',
        title: 'Product Details',
        fields: ['mustHaves', 'materialDetails', 'colorDetails', 'graphicDetails'],
        displayType: 'stacked'
    },
    {
        id: 'sustainability',
        title: 'Sustainability',
        fields: ['sustainabilityTierGoal', 'sustainabilityFocus']
    },
    {
        id: 'performance',
        title: 'Performance',
        fields: [
            'keyLearnings',
            'forecast',
            'geoAdoption',
            'keyDistro',
            'museDoors'
        ],
        displayType: 'stacked'
    },
    {
        id: 'model-details',
        title: 'Model Details',
        fields: [
            'genderStrategy',
            'isXDimension',
            'hasApparelConnection',
            'modelMixCount'
        ]
    }
];
const DETAILS_SECTIONS_OPEN_OVERVIEW = [
    'expression-details',
    'consumer',
    'insights',
    'level',
    'hindsight'
];
const DETAILS_SECTIONS_OPEN = DETAILS_SECTION_CONFIG.map((s) => s.id);
function InspirationElementDisplay({ elements }) {
    if (!elements || elements.length === 0) {
        return (_jsx(Text, { font: "body-2", className: "pds-color-text-secondary", children: "\u2014" }));
    }
    return (_jsx(Stack, { gap: "m", children: elements.map((element, index) => (_jsxs(Stack, { gap: "xs", children: [_jsx(Text, { font: "body-2-strong", children: element.name }), (element.details || []).map((detail, detailIndex) => (_jsx(Text, { font: "body-2", className: "pds-color-text-secondary", children: detail }, detailIndex)))] }, index))) }));
}
/**
 * ExpressionOverviewContent - Expression-specific overview for EntitySidebar
 *
 * Renders expression details in a tabbed layout with Details, More, and Teams tabs.
 * Details tab contains: Target Consumer, Marketplace Insights, Level of Expression, Hindsight/Comp
 * Follows the same pattern as ProjectOverviewContent.
 *
 * Note: ImageGallery is rendered by parent DetailSidebar component.
 */
function ExpressionOverviewContent({ expression }) {
    const team = expression.team;
    const { projects: projectGroups, isLoading: projectsLoading, error: projectsError } = useExpressionProjects(expression.id, expression.museDoors, expression.gender?.id, expression.seasons, expression.gender);
    const projects = projectGroups.filter((group) => group.projects.length > 0);
    const PROJECTS_SECTIONS_OPEN = projects.length > 0 ? [projects[0].id] : [];
    return (_jsxs(Stack, { gap: "m", className: "w-full", children: [_jsx(Link, { to: `/expressions/detail/${expression.handle || expression.id}`, state: {
                    from: '/expressions/view/card',
                    fromLabel: 'Back to Expressions',
                    reopenSidebar: true,
                    sidebarEntity: expression,
                    sidebarEntityType: 'expression'
                }, className: "text-sm underline pds-color-text-primary hover:pds-color-text-secondary", children: "View Full Details" }), _jsxs(Tabs, { defaultValue: "overview", className: "w-full", children: [_jsxs(TabsList, { variant: "overlay", children: [_jsx(TabsTrigger, { value: "overview", children: "Overview" }), _jsx(TabsTrigger, { value: "details", children: "Details" }), _jsx(TabsTrigger, { value: "teams", children: "Teams" }), _jsx(TabsTrigger, { value: "projects", children: "Projects" })] }), _jsx(TabsContent, { value: "overview", children: _jsxs(Stack, { gap: "l", className: "w-full pt-4", children: [_jsx(HighlightedSection, { items: [
                                        { label: 'Theme', value: expression.museDoors },
                                        { label: 'Gender', value: expression.gender?.label },
                                        { label: 'Age', value: expression.age?.label }
                                    ] }), _jsx(HighlightedSection, { items: [
                                        {
                                            label: 'Special Edition',
                                            value: expression.specialEdition ? 'Yes' : 'No'
                                        },
                                        {
                                            label: 'Upcharge',
                                            value: formatUpcharge(expression.upcharge) ?? '—'
                                        },
                                        { label: 'Field of Play', value: expression.fieldOfPlay?.label }
                                    ] }), _jsxs(DetailAccordion, { defaultOpen: DETAILS_SECTIONS_OPEN_OVERVIEW, children: [_jsx(DetailSection, { value: "expression-details", title: "Expression Details", children: _jsxs(Stack, { gap: "m", children: [_jsx(StackedDetail, { label: "Brand", value: expression.brand?.label }), _jsx(StackedDetail, { label: "Division", value: expression.division?.label }), _jsx(StackedDetail, { label: "Expression Intent", value: expression.expressionIntent }), _jsx(StackedDetail, { label: "Seasons", value: expression.seasons?.map((s) => s.label).join(', ') })] }) }), _jsx(DetailSection, { value: "consumer", title: "Target Consumer", children: _jsx(InspirationElementDisplay, { elements: expression.targetConsumerElements || [] }) }), _jsx(DetailSection, { value: "insights", title: "Marketplace Insights", children: _jsx(InspirationElementDisplay, { elements: expression.marketplaceInsightElements || [] }) }), _jsx(DetailSection, { value: "level", title: "Level of Expression", children: _jsxs(Stack, { gap: "m", children: [_jsx(StackedDetail, { label: "Theme", value: expression.museDoors }), _jsx(StackedDetail, { label: "Expression Intent", value: expression.expressionIntent })] }) }), _jsx(DetailSection, { value: "hindsight", title: "Hindsight / Comps", children: _jsx(InspirationElementDisplay, { elements: expression.hindsightCompElements || [] }) })] })] }) }), _jsx(TabsContent, { value: "details", children: _jsx(Stack, { gap: "l", className: "w-full pt-4", children: _jsx(DetailAccordion, { defaultOpen: DETAILS_SECTIONS_OPEN, children: DETAILS_SECTION_CONFIG.map((section) => (_jsx(CategorySection, { entity: expression, section: section, customLabels: CUSTOM_LABELS }, section.id))) }) }) }), _jsx(TabsContent, { value: "teams", children: _jsxs(Stack, { gap: "l", className: "w-full pt-4", children: [_jsx(TeamMemberList, { members: buildExpressionTeamMembers(team), emptyMessage: "No team members assigned" }), expression.createdAt && (_jsxs(Stack, { gap: "xs", className: "pt-4 border-t", children: [_jsx(StackedDetail, { label: "Created", value: new Date(expression.createdAt).toLocaleDateString() }), expression.updatedAt && (_jsx(StackedDetail, { label: "Updated", value: new Date(expression.updatedAt).toLocaleDateString() }))] }))] }) }), _jsx(TabsContent, { value: "projects", children: _jsx(Stack, { gap: "l", className: "w-full pt-4", children: projectsLoading ? (_jsx(Stack, { className: "items-center py-8", children: _jsx(Spinner, { size: "medium" }) })) : projectsError ? (_jsx(Text, { font: "body-2", className: "pds-color-text-secondary", children: "Failed to load projects" })) : projects.length > 0 ? (_jsx(DetailAccordion, { defaultOpen: PROJECTS_SECTIONS_OPEN, children: projects.map((group) => (_jsx(DetailSection, { value: group.id, title: group.name, children: _jsx(Stack, { children: group.projects.map((project, index) => (_jsx(ProjectMiniCard, { project: project, isFirst: index === 0 }, project.name))) }) }, group.id))) })) : (_jsx(Text, { font: "body-2", className: "pds-color-text-secondary", children: "No projects available" })) }) })] })] }));
}
export default ExpressionOverviewContent;
